import { useState } from "react";
import { supabase } from "./lib/supabase";

const T = {
  bg: "#F2EBDC",
  panel: "#FFFFFF",
  ink: "#1A1815",
  ink2: "#3F3B33",
  muted: "#7A7468",
  border: "#E5DCC3",
  accent: "#00320F",
  accentSoft: "#D9E5DD",
  pinkSoft: "#F4E0EB",
  pinkInk: "#5C2A4A",
  cost: "#9E1B1B",
  costSoft: "#F4D3D0",
};

const fontDisplay = `'Fraunces', Georgia, serif`;
const fontBody = `'Manrope', system-ui, sans-serif`;

/**
 * LoginScreen handles three flows:
 *   mode = "signin"       email + password sign-in (default)
 *   mode = "request_reset"  request a password reset email
 *   mode = "set_password"   set a new password (after clicking reset email link)
 *
 * The `mode` defaults to "signin", but Root.jsx flips it to "set_password"
 * when it detects the user has just landed from a password-recovery email.
 */
export default function LoginScreen({ initialMode = "signin" }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState(null);

  const reset = () => {
    setStatus("idle");
    setError(null);
  };

  const submitSignIn = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || !password) return;
    if (!supabase) {
      setError("Supabase isn't configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.");
      setStatus("error");
      return;
    }
    setStatus("sending");
    setError(null);
    const { error: e2 } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (e2) {
      // Supabase's "Invalid login credentials" is a deliberately vague message;
      // surface it as-is. Other errors get passed through too.
      setError(e2.message);
      setStatus("error");
    }
    // On success, Root will detect the session change and re-render — no action needed here.
  };

  const submitRequestReset = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    if (!supabase) {
      setError("Supabase isn't configured.");
      setStatus("error");
      return;
    }
    setStatus("sending");
    setError(null);
    const { error: e2 } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: window.location.origin,
    });
    if (e2) {
      setError(e2.message);
      setStatus("error");
    } else {
      setStatus("sent");
    }
  };

  const submitSetPassword = async (e) => {
    e?.preventDefault?.();
    if (!newPassword || newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      setStatus("error");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords don't match.");
      setStatus("error");
      return;
    }
    setStatus("sending");
    setError(null);
    const { error: e2 } = await supabase.auth.updateUser({ password: newPassword });
    if (e2) {
      setError(e2.message);
      setStatus("error");
    }
    // On success, the user is already signed in (the recovery flow logs them in).
    // Root will detect the session and render the app.
  };

  /* ---------- View: request password reset ---------- */
  if (mode === "request_reset") {
    return (
      <Shell subtitle="Enter your email — we'll send you a link to set a new password.">
        {status === "sent" ? (
          <Sent
            email={email}
            label="reset link"
            onTryAgain={() => { reset(); }}
          />
        ) : (
          <form onSubmit={submitRequestReset}>
            <FieldLabel>Email</FieldLabel>
            <EmailInput value={email} onChange={setEmail} autoFocus />

            <PrimaryButton
              type="submit"
              disabled={status === "sending" || !email.trim()}
              loading={status === "sending"}
            >
              Send reset link
            </PrimaryButton>

            {status === "error" && error && <ErrorBox>{error}</ErrorBox>}

            <SecondaryLink onClick={() => { reset(); setMode("signin"); }}>
              ← Back to sign in
            </SecondaryLink>
          </form>
        )}
      </Shell>
    );
  }

  /* ---------- View: set new password (after reset link) ---------- */
  if (mode === "set_password") {
    return (
      <Shell subtitle="Set a new password for your account.">
        <form onSubmit={submitSetPassword}>
          <FieldLabel>New password</FieldLabel>
          <PasswordInput
            value={newPassword}
            onChange={setNewPassword}
            autoComplete="new-password"
            autoFocus
          />

          <FieldLabel>Confirm new password</FieldLabel>
          <PasswordInput
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
          />

          <PrimaryButton
            type="submit"
            disabled={status === "sending" || !newPassword || !confirmPassword}
            loading={status === "sending"}
          >
            Set password
          </PrimaryButton>

          {status === "error" && error && <ErrorBox>{error}</ErrorBox>}

          <div style={{ fontSize: 11, color: T.muted, marginTop: 12, lineHeight: 1.5 }}>
            Minimum 8 characters. We recommend something memorable but not used elsewhere.
          </div>
        </form>
      </Shell>
    );
  }

  /* ---------- View: sign in (default) ---------- */
  return (
    <Shell subtitle="Sign in with your email and password.">
      <form onSubmit={submitSignIn}>
        <FieldLabel>Email</FieldLabel>
        <EmailInput value={email} onChange={setEmail} autoFocus />

        <FieldLabel>Password</FieldLabel>
        <PasswordInput
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
        />

        <PrimaryButton
          type="submit"
          disabled={status === "sending" || !email.trim() || !password}
          loading={status === "sending"}
        >
          Sign in
        </PrimaryButton>

        {status === "error" && error && <ErrorBox>{error}</ErrorBox>}

        <SecondaryLink onClick={() => { reset(); setMode("request_reset"); }}>
          Forgot password?
        </SecondaryLink>
      </form>
    </Shell>
  );
}

/* ============================================================
   Reusable form primitives
   ============================================================ */

function Shell({ subtitle, children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: fontBody,
        padding: 24,
      }}
    >
      <div
        style={{
          background: T.panel,
          borderRadius: 12,
          padding: "36px 32px",
          maxWidth: 420,
          width: "100%",
          border: `1px solid ${T.border}`,
          boxShadow: "0 2px 8px rgba(26,24,21,0.04)",
        }}
      >
        <div
          style={{
            fontFamily: fontDisplay,
            fontSize: 28,
            fontWeight: 500,
            letterSpacing: "-0.02em",
            color: T.ink,
            marginBottom: 4,
          }}
        >
          Syrup <span style={{ fontStyle: "italic", color: T.accent, fontWeight: 400 }}>cash flow</span>
        </div>
        <div style={{ fontSize: 13, color: T.muted, marginBottom: 24 }}>
          {subtitle}
        </div>

        {children}

        <div
          style={{
            marginTop: 28,
            paddingTop: 18,
            borderTop: `1px solid ${T.border}`,
            fontSize: 11,
            color: T.muted,
            lineHeight: 1.5,
          }}
        >
          Internal Syrup tool. Access by invitation only — if you can't sign in, ask Oliver.
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <label
      style={{
        display: "block",
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: T.muted,
        fontWeight: 600,
        marginBottom: 6,
      }}
    >
      {children}
    </label>
  );
}

function EmailInput({ value, onChange, autoFocus }) {
  return (
    <input
      type="email"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="you@madewithsyrup.com"
      autoFocus={autoFocus}
      autoComplete="email"
      style={inputStyle()}
    />
  );
}

function PasswordInput({ value, onChange, autoFocus, autoComplete }) {
  return (
    <input
      type="password"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoFocus={autoFocus}
      autoComplete={autoComplete || "current-password"}
      style={inputStyle()}
    />
  );
}

function inputStyle() {
  return {
    width: "100%",
    padding: "10px 12px",
    background: T.bg,
    border: `1px solid ${T.border}`,
    borderRadius: 6,
    fontSize: 14,
    fontFamily: "inherit",
    color: T.ink,
    outline: "none",
    marginBottom: 14,
    boxSizing: "border-box",
  };
}

function PrimaryButton({ children, type, disabled, loading, onClick }) {
  return (
    <button
      type={type || "button"}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        padding: "11px 16px",
        background: T.accent,
        color: "#FBF8F0",
        border: "none",
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 600,
        fontFamily: "inherit",
        cursor: loading ? "wait" : disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {loading ? "Working…" : children}
    </button>
  );
}

function ErrorBox({ children }) {
  return (
    <div
      style={{
        marginTop: 12,
        padding: "8px 12px",
        background: T.costSoft,
        color: T.cost,
        borderRadius: 6,
        fontSize: 12,
        lineHeight: 1.4,
      }}
    >
      {children}
    </div>
  );
}

function SecondaryLink({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        marginTop: 14,
        background: "none",
        border: "none",
        padding: 0,
        color: T.accent,
        textDecoration: "underline",
        cursor: "pointer",
        font: "inherit",
        fontSize: 12,
      }}
    >
      {children}
    </button>
  );
}

function Sent({ email, label, onTryAgain }) {
  return (
    <div
      style={{
        padding: 16,
        background: T.accentSoft,
        borderRadius: 8,
        fontSize: 13,
        color: T.accent,
        lineHeight: 1.5,
      }}
    >
      <strong>Check your inbox.</strong> We've sent a {label} to <strong>{email}</strong>.
      <div style={{ marginTop: 12, fontSize: 12, color: T.muted }}>
        No email after a minute or two? Check spam, or{" "}
        <button
          onClick={onTryAgain}
          style={{
            background: "none", border: "none", padding: 0,
            color: T.accent, textDecoration: "underline", cursor: "pointer",
            font: "inherit",
          }}
        >
          try again
        </button>
        .
      </div>
    </div>
  );
}
